class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class WncColorMediaFilter {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.gallery = productInfo.querySelector('[id^="MediaGallery-"]');
    const configScript = this.gallery?.querySelector('[data-wnc-color-media-config]');
    if (!configScript) return;
    try {
      this.config = JSON.parse(configScript.textContent);
      this.groups = this.buildGroups(this.config);
    } catch (_) {
      return;
    }
    if (!this.groups) return;
    const wrappers = this.gallery.querySelectorAll('swiper-product-gallery[handle] .swiper-wrapper, swiper-product-thumbs[handle] .swiper-wrapper, .main-product__media--grid');
    this.collections = Array.from(wrappers, wrapper => ({
      wrapper,
      nodes: Array.from(wrapper.children).map(node => ({
        node,
        mediaId: String(node.dataset.mediaId || node.querySelector('[data-media-id]')?.dataset.mediaId || '')
      }))
    }));
    const allIds = new Set(this.config.media.map(media => String(media.id)));
    if (!this.collections.length || this.collections.some(collection => collection.nodes.some(item => !allIds.has(item.mediaId)))) {
      this.groups = null;
      return;
    }
    this.apply();
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.refreshSwipers(false), 100);
    };
    window.addEventListener('resize', this.onResize);
  }

  buildGroups(config) {
    if (window.WncColorGallery) return window.WncColorGallery.buildGroups({ ...config, mode: config.override ? 'curated' : 'auto' });
    if (!Array.isArray(config.colors) || config.colors.length < 2 || !Array.isArray(config.media) || !config.media.length || config.complete === false) return null;
    const media = [...config.media].sort((a, b) => a.position - b.position);
    const mediaIds = new Set(media.map(item => String(item.id)));
    const groups = new Map(config.colors.map(color => [color.name, new Set()]));
    if (groups.size !== config.colors.length || mediaIds.size !== media.length) return null;
    if (config.override !== null && config.override !== undefined) {
      if (typeof config.override !== 'object' || Array.isArray(config.override) || Object.keys(config.override).length !== groups.size) return null;
      const assigned = new Set();
      for (const [color, ids] of groups) {
        const overrideIds = config.override[color];
        if (!Array.isArray(overrideIds) || !overrideIds.length) return null;
        for (const id of overrideIds) {
          const key = String(id);
          if (!mediaIds.has(key) || assigned.has(key)) return null;
          assigned.add(key);
          ids.add(key);
        }
      }
      return assigned.size === mediaIds.size ? groups : null;
    }
    const anchorColors = new Map();
    for (const color of config.colors) {
      const anchors = new Set((color.anchors || []).filter(id => id !== null && id !== undefined).map(String));
      if (!anchors.size) return null;
      for (const id of anchors) {
        if (!mediaIds.has(id) || (anchorColors.has(id) && anchorColors.get(id) !== color.name)) return null;
        anchorColors.set(id, color.name);
      }
    }
    if (!anchorColors.has(String(media[0].id))) return null;
    let currentColor;
    for (const item of media) {
      const id = String(item.id);
      if (anchorColors.has(id)) currentColor = anchorColors.get(id);
      groups.get(currentColor)?.add(id);
    }
    return Array.from(groups.values()).every(ids => ids.size) ? groups : null;
  }

  selectedColor() {
    const picker = this.productInfo.querySelector('product-variant-selects');
    if (!picker) return null;
    const selectedIds = new Set(Array.from(picker.querySelectorAll('input:checked, select option:checked'), input => String(input.dataset.optionValueId)));
    return this.config.colors.find(color => selectedIds.has(String(color.optionValueId)))?.name || null;
  }

  apply() {
    if (!this.groups || !this.collections) return;
    const color = this.selectedColor();
    const visibleIds = this.groups.get(color);
    const nextKey = visibleIds ? color : null;
    if (this.hasApplied && this.currentColor === nextKey) return;
    this.hasApplied = true;
    this.currentColor = nextKey;
    this.gallery.querySelectorAll('video').forEach(video => video.pause());
    this.gallery.querySelectorAll('swiper-product-gallery').forEach(gallery => {
      gallery.photoSwipeLightboxInstance?.pswp?.close();
      gallery.resetAllModelViewers?.();
    });
    for (const collection of this.collections) {
      for (const { node } of collection.nodes) node.remove();
      for (const { node, mediaId } of collection.nodes) {
        if (!visibleIds || visibleIds.has(mediaId)) collection.wrapper.appendChild(node);
      }
    }
    this.gallery.dataset.wncColorFiltering = String(Boolean(visibleIds));
    this.refreshSwipers(true);
    this.observeAutoplayVideos();
  }

  observeAutoplayVideos() {
    // Filtering moves media nodes between the DOM and the stored color groups.
    // Observe again after each move so visible videos resume without a click.
    this.videoObserver ||= new IntersectionObserver(entries => {
      entries.forEach(({ target: video, isIntersecting, intersectionRatio }) => {
        if (!isIntersecting || intersectionRatio < 0.5 || !video.autoplay || !video.isConnected || !this.gallery.contains(video) || !video.getClientRects().length) {
          video.pause();
          return;
        }
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        if (!video.paused) return;
        video.play()?.catch(() => {
          // Keep the native controls available if the browser blocks autoplay.
        });
      });
    }, { threshold: 0.5 });
    this.videoObserver.disconnect();
    this.gallery.querySelectorAll('video[autoplay]').forEach(video => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      this.videoObserver.observe(video);
    });
  }

  refreshSwipers(reset) {
    if (!this.gallery?.isConnected) return;
    this.gallery.querySelectorAll('swiper-product-thumbs[handle], swiper-product-gallery[handle]').forEach(gallery => {
      gallery.updateZoomSource?.();
      if (!gallery.checkVisibility?.()) return;
      if (!gallery.swiperInitialized) gallery.init?.();
      const swiper = gallery.instance;
      if (!swiper || swiper.destroyed) return;
      swiper.update();
      if (reset) swiper.slideTo(0, 0);
      swiper.pagination?.render?.();
      swiper.pagination?.update?.();
      swiper.thumbs?.update?.(true);
      if (swiper.params?.autoHeight) swiper.updateAutoHeight?.(0);
      else swiper.wrapperEl?.style.removeProperty('height');
    });
  }

  disconnect() {
    this.videoObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    this.collections?.forEach(({ wrapper, nodes }) => {
      nodes.forEach(({ node }) => wrapper.appendChild(node));
    });
    this.gallery?.removeAttribute('data-wnc-color-filtering');
  }
}

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.updateSizeAvailability();
    this.closest('product-info')?.syncSizeSelection?.();
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.closest('product-info')?.recordSizeSelection?.(event.target);
      this.updateSelectionMetadata(event);
      this.updateSizeAvailability();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSizeAvailability() {
    let variants;
    let selectedVariant;
    try {
      variants = JSON.parse(this.querySelector('[data-all-variants]')?.textContent || '[]');
      selectedVariant = JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || 'null');
    } catch (_) { return; }
    if (!Array.isArray(variants) || !variants.length || variants.length >= 250) return;
    const groups = [...this.querySelectorAll('.product-form__input')];
    const selections = groups.map((group, index) => group.querySelector('input:checked')?.value ?? group.querySelector('select')?.value ?? selectedVariant?.options?.[index] ?? null);
    for (const group of groups.filter(item => item.hasAttribute('data-wnc-size-option'))) {
      for (const input of group.querySelectorAll('input[data-option-position], select option[data-option-position]')) {
        const position = Number(input.dataset.optionPosition) - 1;
        if (!Number.isInteger(position) || position < 0) continue;
        const available = variants.some(variant => variant.available === true && String(variant.options?.[position]) === String(input.value) && variant.options.every((value, index) => index === position || selections[index] === null || String(value) === String(selections[index])));
        input.dataset.wncAvailable = String(available);
      }
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).filter(input => input.dataset.optionValueId).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      explicitSizes = new Map();
      sizeSelectionLoading = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeSizeSelection();
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.colorMediaFilter = new WncColorMediaFilter(this);
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }
        this.ensurePurchasableColor();
      }

      initializeSizeSelection() {
        this._sizeProductId = String(this.dataset.productId || this.dataset.url);
        const submit = this.querySelector('[data-wnc-variant-disabled]');
        this._sizeVariantDisabled = submit?.dataset.wncVariantDisabled === 'true';
        this.updateSizePurchaseAvailability(submit);
        this.addEventListener('submit', event => {
          if (!event.target.matches('form[id^="product-form-"], form[data-type="add-to-cart-form"]')) return;
          if (this.validateSizeSelection()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
        this.addEventListener('click', event => {
          if (!event.target.closest?.('button[name="add"], [data-wnc-dynamic-checkout], .shopify-payment-button, shopify-accelerated-checkout')) return;
          if (!this.hasPendingSizeSelection()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.validateSizeSelection();
        }, true);
        this.syncSizeSelection();
      }

      updateSizePurchaseAvailability(submit) {
        if (!submit) return;
        if (submit.dataset.wncProductAvailable !== undefined) this._sizeProductAvailable = submit.dataset.wncProductAvailable === 'true';
        this._sizeQuantityDisabled = submit.dataset.wncQuantityDisabled === 'true';
      }

      recordSizeSelection(target) {
        const group = target.closest?.('[data-wnc-size-option]');
        if (!group || !this.variantSelectors?.contains(group)) return;
        const value = target.value;
        const position = Number(group.dataset.wncSizePosition);
        if (!position || !value) return;
        this.explicitSizes.set(position, String(value));
      }

      hasPendingSizeSelection() {
        const requiresSize = this.variantSelectors?.querySelector('[data-wnc-size-option]') || this.querySelector('product-form[data-wnc-requires-size]');
        return Boolean(requiresSize && (this.sizeSelectionLoading || this._sizePending !== false));
      }

      validateSizeSelection() {
        if (!this.hasPendingSizeSelection()) return true;
        const message = window.variantStrings?.selectSize || 'Selecionar tamanho';
        this.productForm?.handleErrorMessage?.(message);
        const group = Array.from(this.variantSelectors?.querySelectorAll('[data-wnc-size-option]') || []).find(item => !this.explicitSizes.has(Number(item.dataset.wncSizePosition))) || this.variantSelectors?.querySelector('[data-wnc-size-option]');
        if (typeof window.wncHighlightSizeGroup === 'function') {
          window.wncHighlightSizeGroup(group);
        } else {
          const input = group?.querySelector('select, input:not(:disabled)[data-wnc-available="true"], input:not(:disabled)');
          group?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
          input?.focus?.({ preventScroll: true });
        }
        return false;
      }

      syncSizeSelection() {
        const picker = this.variantSelectors;
        const groups = Array.from(picker?.querySelectorAll('.product-form__input') || []);
        const sizeGroups = groups.filter(group => group.hasAttribute('data-wnc-size-option'));
        if (!sizeGroups.length && !this.querySelector('product-form[data-wnc-requires-size]')) return;
        const productId = String(this.dataset.productId || this.dataset.url);
        if (this._sizeProductId !== productId) {
          this.explicitSizes.clear();
          this._sizeProductId = productId;
        }
        let variants = [];
        let preview = null;
        try {
          variants = JSON.parse(picker?.querySelector('[data-all-variants]')?.textContent || '[]');
          preview = this.getSelectedVariant(this);
        } catch (_) { /* Invalid variant data keeps purchasing blocked. */ }
        const selections = groups.map((group, index) => group.hasAttribute('data-wnc-size-option')
          ? this.explicitSizes.get(Number(group.dataset.wncSizePosition)) || null
          : group.querySelector('input:checked')?.value ?? group.querySelector('select')?.value ?? preview?.options?.[index] ?? null);
        for (const group of sizeGroups) {
          const position = Number(group.dataset.wncSizePosition);
          let value = this.explicitSizes.get(position);
          if (value && !this.sizeSelectionLoading) {
            const candidates = variants.length && variants.length < 250 ? variants : [preview].filter(Boolean);
            const available = candidates.some(variant => variant.available && String(variant.options?.[position - 1]) === value && variant.options.every((option, index) => index === position - 1 || selections[index] === null || String(option) === String(selections[index])));
            if (!available) {
              this.explicitSizes.delete(position);
              value = undefined;
            }
          }
          group.querySelectorAll('input[type="radio"]').forEach(input => {
            input.checked = Boolean(value && input.value === value);
            input.toggleAttribute('checked', input.checked);
          });
          const select = group.querySelector('select');
          if (select) {
            select.value = value || '';
            Array.from(select.options).forEach(option => option.toggleAttribute('selected', option.value === (value || '')));
            const dropdownLabel = group.querySelector('dropdown-input [data-label]');
            if (dropdownLabel) dropdownLabel.textContent = value || window.variantStrings?.selectSize || 'Selecionar tamanho';
            group.querySelectorAll('dropdown-input [data-item]').forEach(item => item.classList.toggle('is-active', item.querySelector('button')?.dataset.value === (value || '')));
          }
          const label = group.querySelector('legend [data-selected-value]');
          if (label) label.textContent = value || window.variantStrings?.selectSize || 'Selecionar tamanho';
        }
        const confirmed = sizeGroups.length > 0 && preview?.available && sizeGroups.every(group => {
          const position = Number(group.dataset.wncSizePosition);
          return this.explicitSizes.has(position) && this.explicitSizes.get(position) === String(preview.options?.[position - 1]);
        });
        this._sizePending = !confirmed || this.sizeSelectionLoading;
        this.toggleAttribute('data-wnc-size-pending', this._sizePending);
        const variantInputDisabled = this._sizePending || this._sizeVariantDisabled;
        this.querySelectorAll(`form[id^="product-form-"] input[name="id"]`).forEach(input => {
          input.value = variantInputDisabled ? '' : String(preview.id);
          input.disabled = variantInputDisabled;
        });
        const submit = this.querySelector('[id^="ProductSubmitButton-"]');
        const productAvailable = this._sizeProductAvailable ?? variants.some(variant => variant.available);
        const stockDisabled = this._sizePending
          ? !productAvailable || Boolean(preview?.available && this._sizeQuantityDisabled)
          : Boolean(this._sizeVariantDisabled);
        const busy = this.sizeSelectionLoading || submit?.getAttribute('aria-disabled') === 'true' || submit?.classList?.contains?.('loading');
        const buttonDisabled = Boolean(stockDisabled || busy);
        if (submit) {
          if (!busy) this.productForm?.toggleSubmitButton?.(stockDisabled, window.variantStrings?.soldOut);
          submit.disabled = buttonDisabled;
          if (this._sizePending && !buttonDisabled && submit.querySelector('span')) submit.querySelector('span').textContent = window.variantStrings?.addToCart;
        }
        this.querySelectorAll('.product-bar__submit').forEach(button => {
          button.disabled = buttonDisabled;
          const label = button.querySelector('span');
          if (label) label.textContent = submit?.querySelector('span')?.textContent || window.variantStrings?.addToCart;
        });
        const stickyOptions = this.querySelectorAll('.product__sticky-cart--opt-label');
        sizeGroups.forEach(group => {
          const position = Number(group.dataset.wncSizePosition);
          if (stickyOptions[position - 1]) stickyOptions[position - 1].textContent = this.explicitSizes.get(position) || window.variantStrings?.selectSize || 'Selecionar tamanho';
        });
        this.querySelectorAll('[data-wnc-dynamic-checkout]').forEach(wrapper => {
          wrapper.hidden = variantInputDisabled || buttonDisabled;
          wrapper.toggleAttribute('inert', wrapper.hidden);
        });
      }

      ensurePurchasableColor() {
        const picker = this.variantSelectors;
        const position = Number(picker?.dataset.colorOptionPosition) - 1;
        if (!Number.isInteger(position) || position < 0) return false;
        let variants;
        try {
          variants = JSON.parse(picker.querySelector('[data-all-variants]')?.textContent || '[]');
        } catch (_) {
          return false;
        }
        if (!Array.isArray(variants)) return false;
        const selected = this.getSelectedVariant(this);
        const color = selected?.options?.[position];
        if (!color || variants.some(variant => variant.available && variant.options?.[position] === color)) return false;
        const fallback = variants.find(variant => variant.available);
        if (!fallback) return false;
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', fallback.id);
        url.searchParams.set('section_id', this.sectionId);
        this.renderProductInfo({
          requestUrl: `${url.pathname}${url.search}`,
          targetId: picker.id,
          callback: this.handleUpdateProductInfo(this.dataset.url)
        });
        return true;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.colorMediaFilter?.disconnect();
        this.abortController?.abort();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        this.colorMediaFilter?.apply();
        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.sizeSelectionLoading = true;
        this.syncSizeSelection();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              this.sizeSelectionLoading = false;
              this.explicitSizes.clear();
              this.syncSizeSelection();
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          const currentPicker = this.variantSelectors;
          const chart = currentPicker?.querySelector('.Clean_Size_Chart');
          const preserveChart = chart && productVariantSelects.querySelector('.Clean_Size_Chart');
          if (preserveChart) chart.remove();
          HTMLUpdateUtility.viewTransition(
            currentPicker,
            productVariantSelects,
            this.preProcessHtmlCallbacks,
            preserveChart ? [newPicker => {
              newPicker.querySelector('.Clean_Size_Chart')?.replaceWith(chart);
            }] : []
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);
          const serverSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.updateSizePurchaseAvailability(serverSubmit);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.colorMediaFilter?.apply();
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            this.updateVariantMedia(html);
          }

          if (!variant) {
            this.sizeSelectionLoading = false;
            this._sizeVariantDisabled = true;
            this.setUnavailable();
            this.syncSizeSelection();
            return;
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this._sizeVariantDisabled = serverSubmit?.hasAttribute('data-wnc-variant-disabled')
            ? serverSubmit.dataset.wncVariantDisabled === 'true'
            : serverSubmit?.hasAttribute('disabled') ?? true;
          this.productForm?.toggleSubmitButton(
            this._sizeVariantDisabled,
            window.variantStrings.soldOut
          );
          this.sizeSelectionLoading = false;
          this.syncSizeSelection();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); // Update the Swiper instance
          } else {
            swiperSlide.init(); // If the instance is not available, reinitialize
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          const pendingSize = this.hasPendingSizeSelection();
          input.value = pendingSize ? '' : variantId ?? "";
          input.disabled = pendingSize || !variantId;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");

          if (!percentageElement) return;
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && !DeviceDetector.isMobile()
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = this.hasAttribute('data-wnc-requires-size');
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.closest('product-info')?.validateSizeSelection?.() === false) return;
        if (
          this.submitButton.getAttribute("aria-disabled") === "true"
        )
          return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.hasAttribute('data-has-pre-order')) {
          formData.append('properties[Pre-order]', '✓');
        }
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        let itemAdded = false;
        return fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            itemAdded = true;
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
            if (itemAdded) {
              window.location.assign(window.routes.cart_url);
            } else {
              this.handleErrorMessage(window.cartStrings?.error || 'Não foi possível adicionar o produto. Tente novamente.');
            }
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (el.classList.contains('product__pickup-availabilities')  || el.classList.contains('product__block--custom-liquid')) return;
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});





